import React, { useEffect, useRef } from 'react';
import { MedicalProfileFiles } from '../../../../../primary-record-types';
import { colors } from '../../../../../utils/theme';
import { mainContentPadding } from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import TranscriptLineDisplay from './TranscriptLineDisplay';
import { ScrollView } from 'react-native';
import PressableDynamicOpacity from '../../../../v2BaseComponents/PressableDynamicOpacity';
import { PRScrollView } from '../../../../shared/Scroll';

export type TranscriptDisplayOwnProps = {
    transcript: MedicalProfileFiles.Transcript;
    position: number | null;
    isPlaying: boolean;
    onSetPosition?: (position: number) => void;
};

export type TranscriptDisplayProps = TranscriptDisplayOwnProps;

type YPositions = Record<number, number | null>;

const TranscriptDisplay: React.FC<TranscriptDisplayProps> = (props) => {
    const transcriptScrollRef = useRef<ScrollView | null>(null);
    const [yPositions, setYPositions] = React.useState<YPositions>(() => {
        const returnValue: YPositions = {};
        props.transcript.sections.forEach((section, index) => {
            returnValue[index] = null;
        });
        return returnValue;
    });
    const activeIndex = React.useMemo<number | null>(() => {
        if (props.position === null || !props.isPlaying) return null;

        const targetSectionIndex = props.transcript.sections.findIndex(
            (section) => section.startTime <= props.position! && section.endTime >= props.position!,
        );

        return targetSectionIndex === -1 ? null : targetSectionIndex;
    }, [props.isPlaying, props.position, props.transcript.sections]);
    useEffect(() => {
        if (activeIndex !== null && yPositions[activeIndex] !== null && transcriptScrollRef.current) {
            transcriptScrollRef.current.scrollTo({
                x: 0,
                y: yPositions[activeIndex]! + 8,
                animated: true,
            });
        }
    }, [activeIndex, props.isPlaying, yPositions]);

    return (
        <PRScrollView
            style={{
                width: '100%',
                height: '100%',
                borderRadius: 8,
                borderColor: colors.Dividers,
                padding: mainContentPadding,
                borderWidth: 1,
            }}
            ref={transcriptScrollRef}
        >
            <Stack vertical space={contentStackSpacing}>
                {props.transcript.sections.map((section, index) => {
                    const layoutHandler = (yPosition: number) => {
                        setYPositions((prev) => ({
                            ...prev,
                            [index]: yPosition,
                        }));
                    };
                    const content = (
                        <TranscriptLineDisplay
                            key={index}
                            line={section}
                            isActive={
                                props.position !== null &&
                                section.startTime <= props.position &&
                                section.endTime >= props.position
                            }
                            isPlaying={props.isPlaying}
                            onSetYPosition={props.onSetPosition ? layoutHandler : undefined}
                        />
                    );

                    return props.onSetPosition ? (
                        <PressableDynamicOpacity
                            key={index}
                            onPress={() => props.onSetPosition?.(section.startTime)}
                            onLayout={(e) => layoutHandler(e.nativeEvent.layout.y)}
                        >
                            {content}
                        </PressableDynamicOpacity>
                    ) : (
                        content
                    );
                })}
            </Stack>
        </PRScrollView>
    );
};

export default React.memo(TranscriptDisplay);
